import { useEffect, useMemo, useState } from "react"
import { useSearch } from "features/search/context"
import { DpcRecord, useListDpcResult, useListDpcResultBySisetu } from "interface/dpc"
import { useListSearchsDisease } from "interface/search/disease"
import { SisetuResult } from "interface/sisetu"
import { CompareModeType, CompareRecord, Disease, Mdc } from "features/dpcResult/types"

type useSisetuDpcProps = {
	sisetu: SisetuResult
}
export const useSisetuDpc = ({
	sisetu
}: useSisetuDpcProps) => {
	const search = useSearch()

	const [selectedYear, setSelectedYear] = useState<number | null>(null)
	const [selectedMdcCode, setSelectedMdcCode] = useState<string | null>(null)
	const [selectedDpcCode, setSelectedDpcCode] = useState<string | null>(null)
	const [compareMode, setCompareMode] = useState<CompareModeType>('iryoken')

	const diseasesState = useListSearchsDisease(search?.searchId)
	const resultState = useListDpcResultBySisetu(sisetu.sisetuCode)
	const compareState = useListDpcResult(
		compareMode === 'nendo' ? undefined : selectedDpcCode,
		compareMode === 'todofuken' ? (sisetu.juusyoKenCode ?? undefined) : undefined,
		compareMode === 'iryoken' ? (sisetu.iryoukenCode ?? undefined) : undefined,
		selectedYear ?? undefined
	)

	const { defaultDpcCode, defaultMdcCode } = useMemo(
		() => {
			const dpcCodeList = diseasesState.data?.list
				.flatMap(disease => disease.dpcCode ?? []) ?? []
			const defaultDpcCode = dpcCodeList.length > 0 ? dpcCodeList[0] : undefined
			const defaultMdcCode = defaultDpcCode?.substring(0, 2)
			return { defaultDpcCode, defaultMdcCode }
		},
		[diseasesState]
	)

	const { yearList, defaultYear } = useMemo(
		() => {
			const result = resultState.data?.list || []
			const yearList: {
				year: number
				maxCasesMdc: number
				maxCasesDpc: number
				mdcList: Mdc[]
				dpcList: Disease[]
			}[] = []
			for (const { mdcCode, mdcName, dpcCode, diseaseName, record } of result) {
				for (const { year, surgery, treatment1, treatment2} of record) {
					const maxCases = Math.max(surgery?.totalCases ?? 0, treatment1?.totalCases ?? 0, treatment2?.totalCases ?? 0)
					if (maxCases === 0) continue

					let y = yearList.find(y => y.year === year)
					if (!y) {
						y = { year, maxCasesMdc: 0, maxCasesDpc: 0, mdcList: [], dpcList: [] }
						yearList.push(y)
					}
					y.maxCasesDpc = Math.max(y.maxCasesDpc, maxCases)

					const mdc = y.mdcList.find(mdc => mdc.code === mdcCode)
					if (!mdc) {
						y.mdcList.push({ code: mdcCode, name: mdcName, totalCases: maxCases, maxCases })
						y.maxCasesMdc = Math.max(y.maxCasesMdc, maxCases)
					} else {
						mdc.maxCases = Math.max(mdc.maxCases, maxCases)
						mdc.totalCases += maxCases
						y.maxCasesMdc = Math.max(y.maxCasesMdc, mdc.totalCases)
					}

					const dpc = y.dpcList.find(dpc => dpc.code === dpcCode)
					if (!dpc) {
						y.dpcList.push({ code: dpcCode, name: diseaseName, totalCases: maxCases })
					} else {
						dpc.totalCases = Math.max(dpc.totalCases, maxCases)
					}
				}
			}
			yearList.sort((a, b) => a.year - b.year)
			yearList.forEach(y => {
				y.mdcList.sort((a, b) => a.code.localeCompare(b.code))
				y.dpcList.sort((a, b) => a.code.localeCompare(b.code))
			})
			const defaultYear = yearList.length > 0 ? yearList[yearList.length - 1].year : null
			return {
				yearList,
				defaultYear
			}
		},
		[resultState.data]
	)

	const nendoResult = useMemo(
		() => {
			const result = resultState.data?.list || []
			const nendoResult: DpcRecord[] = []
			for (const { dpcCode, record } of result) {
				if (dpcCode !== selectedDpcCode) continue
				for (const { year, surgery, treatment1, treatment2} of record) {
					const maxCases = Math.max(surgery?.totalCases ?? 0, treatment1?.totalCases ?? 0, treatment2?.totalCases ?? 0)
					if (maxCases === 0) continue
					nendoResult.push({ year, surgery, treatment1, treatment2 })
				}
			}
			return nendoResult
		},
		[resultState.data, selectedDpcCode]
	)

	const currentYear = useMemo(
		() => {
			return yearList.find(y => y.year === selectedYear) ?? null
		},
		[yearList, selectedYear]
	)

	const mdcList = useMemo(
		() => {
			return currentYear?.mdcList ?? []
		},
		[currentYear]
	)

	const dpcList = useMemo(
		() => {
			return (selectedMdcCode ? currentYear?.dpcList.filter(
				d => d.code.startsWith(selectedMdcCode)
			) : null) ?? []
		},
		[currentYear, selectedMdcCode]
	)

	const currentDisease = useMemo(
		() => {
			return dpcList.find(dpc => dpc.code === selectedDpcCode) ?? null
		},
		[dpcList, selectedDpcCode]
	)

	const compareResult = useMemo<CompareRecord[]>(
		() => {
			const dpcCode = currentDisease?.code
			if (!dpcCode) return []
			const compareResult = (compareState.data?.list ?? []).flatMap(
				result => {
					const cases = Math.max(
						result.surgery[dpcCode]?.totalCases ?? 0,
						result.treatment1[dpcCode]?.totalCases ?? 0,
						result.treatment2[dpcCode]?.totalCases ?? 0
					)
					if (cases === undefined) return []
					return {
						sisetu: result.sisetu,
						cases
					}
				}
			)
			compareResult.sort((a, b) => b.cases - a.cases)
			return compareResult
		},
		[compareState.data, currentDisease]
	)

	useEffect(
		() => {
			const validSelectedYear = (
				(yearList.length === 0) ? null :
				(selectedYear && yearList.find(y => selectedYear === y.year)) ? selectedYear :
				(defaultYear && yearList.find(y => defaultYear === y.year)) ? defaultYear :
				yearList[0]?.year ?? null
			)
			setSelectedYear(validSelectedYear)
		},
		[yearList, setSelectedYear, selectedYear, defaultYear]
	)

	useEffect(
		() => {
			const validSelectedMdcCode = (
				(mdcList.length === 0) ? null :
				(selectedMdcCode && mdcList.find(d => selectedMdcCode === d.code)) ? selectedMdcCode :
				(defaultMdcCode && mdcList.find(d => defaultMdcCode === d.code)) ? defaultMdcCode :
				mdcList[0]?.code ?? null
			)
			setSelectedMdcCode(validSelectedMdcCode)
		},
		[mdcList, setSelectedMdcCode, selectedMdcCode, defaultMdcCode]
	)

	useEffect(
		() => {
			const validSelectedDpcCode = (
				(dpcList.length === 0) ? null :
				(selectedDpcCode && dpcList.find(d => selectedDpcCode === d.code)) ? selectedDpcCode :
				(defaultDpcCode && dpcList.find(d => defaultDpcCode === d.code)) ? defaultDpcCode :
				dpcList[0]?.code ?? null
			)
			setSelectedDpcCode(validSelectedDpcCode)
		},
		[dpcList, setSelectedDpcCode, selectedDpcCode, defaultDpcCode]
	)

	return {
		loading: diseasesState.loading || resultState.loading || compareState.loading,
		error: [diseasesState.error, resultState.error, compareState.error],
		selectedYear, setSelectedYear, yearList, currentYear,
		selectedMdcCode, setSelectedMdcCode, mdcList,
		selectedDpcCode, setSelectedDpcCode, dpcList, currentDisease,
		compareMode, setCompareMode, compareResult, nendoResult
	}
}